import { create, type UseBoundStore, type StoreApi } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import type { BatchDraftExport, BatchDraftExportV1, BatchDraftInstance } from '@pantry/shared';
import { BATCH_DRAFT_STORAGE_KEY, BATCH_DRAFT_EXPIRY_MS, getProductGroupKey } from '@pantry/shared';
import {
  createDraftItem,
  updateDraftItem as updateDraftItemLogic,
  removeDraftItem as removeDraftItemLogic,
  incrementItemByBarcode,
  addInstanceByBarcode as addInstanceByBarcodeLogic,
  toggleItemSelection as toggleItemSelectionLogic,
  mergeSelectedItems as mergeSelectedItemsLogic,
  exportDraftData,
  parseDraftImport,
  validateDraftExport,
  addInstanceToItem as addInstanceToItemLogic,
  removeInstance as removeInstanceLogic,
  updateInstance as updateInstanceLogic,
} from '@pantry/shared/batch';
import type { BatchEntryStateBase } from './batch-entry-types';
import type { CreateDraftItemInput } from '@pantry/shared/batch';

/**
 * Storage adapter interface — accepts both sync (localStorage) and
 * async (AsyncStorage) backends via union return types.
 */
export interface StorageAdapter {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<void> | void;
  removeItem(key: string): Promise<void> | void;
}

/**
 * State shape returned by createBatchEntryStore.
 * deactivate and clearDraftItems are always async so both platforms
 * can await storage operations uniformly.
 */
export interface BatchEntryFactoryState extends BatchEntryStateBase {
  /** Deactivate batch entry mode and clear everything */
  deactivate: () => Promise<void>;
  /** Clear all draft items without deactivating */
  clearDraftItems: () => Promise<void>;
}

/**
 * Factory that creates a Zustand batch entry store backed by the
 * provided StorageAdapter. Both web (localStorage) and mobile
 * (AsyncStorage) use this factory with platform-specific adapters.
 */
export function createBatchEntryStore(storage: StorageAdapter) {
  const store = create<BatchEntryFactoryState>()(
    subscribeWithSelector((set, get) => ({
      isActive: false,
      isContinuousScanMode: false,
      defaultStorageLocationId: null,
      draftItems: [],
      isSubmitting: false,
      lastError: null,
      isMultiSelectMode: false,
      selectedClientIds: new Set<string>(),

      activate: () => set({ isActive: true }),

      deactivate: async () => {
        try {
          await storage.removeItem(BATCH_DRAFT_STORAGE_KEY);
        } catch (e) {
          console.warn('Batch store: failed to clear draft on deactivate', e);
        }
        set({
          isActive: false,
          isContinuousScanMode: false,
          defaultStorageLocationId: null,
          draftItems: [],
          isSubmitting: false,
          lastError: null,
          isMultiSelectMode: false,
          selectedClientIds: new Set<string>(),
        });
      },

      setDefaultStorageLocation: (id) => set({ defaultStorageLocationId: id }),

      setContinuousScanMode: (enabled) => set({ isContinuousScanMode: enabled }),

      addDraftItem: (item: CreateDraftItemInput) => {
        const [newItem, clientId] = createDraftItem(item, nanoid);
        set((state) => ({
          draftItems: [...state.draftItems, newItem],
        }));
        return clientId;
      },

      updateDraftItem: (clientId, updates) =>
        set((state) => ({
          draftItems: updateDraftItemLogic(state.draftItems, clientId, updates),
        })),

      removeDraftItem: (clientId) =>
        set((state) => {
          const result = removeDraftItemLogic(state.draftItems, state.selectedClientIds, clientId);
          return {
            draftItems: result.items,
            selectedClientIds: result.selectedClientIds,
          };
        }),

      incrementQuantityByBarcode: (barcode, quantity = 1) => {
        const state = get();
        const result = incrementItemByBarcode(state.draftItems, barcode, quantity);
        if (result.found) {
          set({ draftItems: result.items });
        }
        return result.found;
      },

      addInstanceByBarcode: (barcode, quantity = 1) => {
        const state = get();
        const result = addInstanceByBarcodeLogic(state.draftItems, barcode, nanoid, quantity);
        if (result.clientId) {
          set({ draftItems: result.items });
        }
        return result.clientId;
      },

      clearDraftItems: async () => {
        try {
          await storage.removeItem(BATCH_DRAFT_STORAGE_KEY);
        } catch (e) {
          console.warn('Batch store: failed to clear draft items from storage', e);
        }
        set({ draftItems: [] });
      },

      setSubmitting: (submitting) => set({ isSubmitting: submitting }),

      setError: (error) => set({ lastError: error }),

      exportDraft: () => {
        const state = get();
        return exportDraftData(state.draftItems, state.defaultStorageLocationId);
      },

      importDraft: (data: BatchDraftExport | BatchDraftExportV1) => {
        const result = parseDraftImport(data, nanoid);
        if (!result) {
          // Draft expired, clean up storage (fire-and-forget, safe for async adapters)
          Promise.resolve(storage.removeItem(BATCH_DRAFT_STORAGE_KEY)).catch((e) => {
            console.warn('Batch store: failed to clear expired draft from storage', e);
          });
          return;
        }

        set({
          isActive: true,
          draftItems: result.items,
          defaultStorageLocationId: result.defaultStorageLocationId,
        });
      },

      getDraftCount: () => get().draftItems.length,

      findByBarcode: (barcode) => get().draftItems.find((item) => item.barcode === barcode),

      findByProductId: (productId) => get().draftItems.find((item) => item.productId === productId),

      findByGroupKey: (key) => get().draftItems.find((item) => getProductGroupKey(item) === key),

      // Multi-select actions
      enterMultiSelectMode: () =>
        set({ isMultiSelectMode: true, selectedClientIds: new Set<string>() }),

      exitMultiSelectMode: () =>
        set({ isMultiSelectMode: false, selectedClientIds: new Set<string>() }),

      toggleItemSelection: (clientId) =>
        set((state) => ({
          selectedClientIds: toggleItemSelectionLogic(state.selectedClientIds, clientId),
        })),

      mergeSelectedItems: () => {
        const state = get();
        const result = mergeSelectedItemsLogic(state.draftItems, state.selectedClientIds);
        if (result.success) {
          set({
            draftItems: result.items,
            isMultiSelectMode: false,
            selectedClientIds: new Set<string>(),
          });
        }
        return result.success;
      },

      // Instance management actions
      addInstanceToItem: (clientId, instance: Omit<BatchDraftInstance, 'instanceId'>) => {
        const state = get();
        const result = addInstanceToItemLogic(state.draftItems, clientId, instance, nanoid);
        set({ draftItems: result.items });
        return result.instanceId;
      },

      removeInstance: (clientId, instanceId) =>
        set((state) => {
          const result = removeInstanceLogic(
            state.draftItems,
            state.selectedClientIds,
            clientId,
            instanceId
          );
          return {
            draftItems: result.items,
            selectedClientIds: result.selectedClientIds,
          };
        }),

      updateInstance: (clientId, instanceId, updates) =>
        set((state) => ({
          draftItems: updateInstanceLogic(state.draftItems, clientId, instanceId, updates),
        })),
    }))
  );

  // Subscribe to state changes and persist to storage
  store.subscribe(
    (state) => ({
      draftItems: state.draftItems,
      defaultStorageLocationId: state.defaultStorageLocationId,
    }),
    async (current) => {
      if (current.draftItems.length > 0) {
        try {
          const data: BatchDraftExport = {
            items: current.draftItems,
            defaultStorageLocationId: current.defaultStorageLocationId,
            expiresAt: Date.now() + BATCH_DRAFT_EXPIRY_MS,
            version: 2,
          };
          await storage.setItem(BATCH_DRAFT_STORAGE_KEY, JSON.stringify(data));
        } catch {
          // Quota exceeded or serialization failure — benign, draft won't persist
        }
      } else {
        // Items emptied via removal — clear stale draft from storage
        try {
          await storage.removeItem(BATCH_DRAFT_STORAGE_KEY);
        } catch (e) {
          console.warn('Batch store: failed to clear empty draft from storage', e);
        }
      }
    },
    {
      equalityFn: (a, b) =>
        a.draftItems === b.draftItems && a.defaultStorageLocationId === b.defaultStorageLocationId,
    }
  );

  return store;
}

/**
 * Extend a Zustand store with platform-specific fields.
 * Injects `extension` into the store state and returns a properly typed handle.
 *
 * This is the safe way to add fields like `isHydrated` / `hydrate()` on mobile
 * without manually re-declaring the store's type signature.
 *
 * SAFETY CONSTRAINTS:
 * - Extension functions must NOT reference the returned store handle
 *   synchronously during construction — the handle is not yet assigned.
 *   Async or deferred references (called after module evaluation) are safe.
 * - The cast below is sound because Zustand's `setState` merges partials
 *   into existing state, so after this call the store truly contains TBase & TExt.
 *   TypeScript can't infer this from `setState`'s signature alone.
 */
export function extendStore<TBase, TExt>(
  store: UseBoundStore<StoreApi<TBase>>,
  extension: TExt
): UseBoundStore<StoreApi<TBase & TExt>> {
  // Merge extension fields into store state; safe because setState does a shallow merge
  (store.setState as (partial: TExt) => void)(extension);
  return store as UseBoundStore<StoreApi<TBase & TExt>>;
}

/**
 * Factory for pending-draft helper functions backed by the given adapter.
 * Both helpers are async so they work uniformly across sync and async backends.
 */
export function createPendingDraftHelpers(storage: StorageAdapter) {
  async function getPendingDraft(): Promise<BatchDraftExport | null> {
    try {
      const stored = await storage.getItem(BATCH_DRAFT_STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      if (!validateDraftExport(data)) {
        await storage.removeItem(BATCH_DRAFT_STORAGE_KEY);
        return null;
      }

      return data as BatchDraftExport;
    } catch (e) {
      console.warn('Batch store: failed to read pending draft', e);
      return null;
    }
  }

  async function clearPendingDraft(): Promise<void> {
    try {
      await storage.removeItem(BATCH_DRAFT_STORAGE_KEY);
    } catch (e) {
      console.warn('Batch store: failed to clear pending draft', e);
    }
  }

  return { getPendingDraft, clearPendingDraft };
}
